import React, { useMemo, useEffect, useState, useRef } from "react";
import { exportFromArrayToExcel } from "@/shared/utils/xlsx";
import { exportFromArrayToPdf } from "@/shared/utils/pdf";
import { useDataTableStateStates } from "./useDataTableState";
import { useReactToPrint } from "react-to-print";
import { removerAcentos } from "@/shared/utils/textos";

export type ColumnType = "string" | "number" | "date";

export interface Column<T> {
  key: keyof T | string;
  header: string;
  type?: ColumnType;

  /**
   * Se verdadeiro a coluna vem inicialmente invisível.
   */
  invisible?: boolean;
  colSpan?: number
  render?: (row: T) => React.ReactNode;
}

interface DataTableProps<T> {
  data?: T[];
  columns: Column<T>[];
  totalRecords?: number;
  initialSortKey?: string;
  pageSizeOptions?: number[];
  renderFilters?: () => React.ReactNode;
  serverSide?: boolean;
  onChange?: () => void;
  states: useDataTableStateStates;
  showFilter?: boolean;
  barras?: boolean;
  disablePagination?: boolean;
  renderSubRow?: (row: T) => React.ReactNode;
  disableColumnsFilter?: boolean;
  disableExportButtons?: boolean;
  getRowClassName?: (row: T) => string;
  getRowStyle?: (row: T) => React.CSSProperties;
  onVisibleColumnsChange?: (visibleColumnKeys: string[]) => void;
}

export function DataTable<T extends object>({
  data,
  columns,
  totalRecords,
  serverSide = false,
  onChange,
  renderFilters,
  states,
  showFilter = true,
  barras = false,
  disablePagination = false,
  renderSubRow,
  disableColumnsFilter = false,
  disableExportButtons = false,
  getRowClassName,
  getRowStyle,
  onVisibleColumnsChange,
}: DataTableProps<T>) {

  const { pageSizeOptions, sortConfig, setSortConfig, currentPage, setCurrentPage, pageSize, setPageSize, filter, setFilter } = states

  const [visibleColumns, setVisibleColumns] = useState<string[]>(columns.filter(a => !a.invisible).map((c) => c.key as string));
  const [scrollBars, setScrollBars] = useState(barras)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const tableRef = useRef<HTMLTableElement>(null);

  const toggleColumn = (key: string) => {
    setVisibleColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  useEffect(() => {
    onVisibleColumnsChange?.(visibleColumns);
  }, [onVisibleColumnsChange, visibleColumns]);

  const toggleRow = (index: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index))
        newSet.delete(index);
      else
        newSet.add(index);
      return newSet;
    });
  };

  // dispara callback no modo serverSide
  useEffect(() => {
    if (serverSide && onChange) {
      onChange();
    }
  }, [serverSide, currentPage, pageSize, sortConfig, states.filter]);

  // funções auxiliares
  const getValue = (obj: any, path: string) =>
    path.split(".").reduce((o, p) => {
      if (o == null)
        return undefined;
      if (!isNaN(Number(p)))
        return o[Number(p)];
      return o[p];
    }, obj);

  const getPlainText = (node: React.ReactNode): string => {
    if (typeof node === "string" || typeof node === "number") {
      return String(node);
    }
    if (Array.isArray(node)) {
      return node.map(getPlainText).join(" ");
    }
    if (React.isValidElement(node)) {
      return getPlainText(node.props.children);
    }
    return "";
  };

  const formatDateForFilter = (value: unknown): string => {
    const d = new Date(value as string | number | Date);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return `${dd}/${mm}/${yyyy}`;
  };

  const NON_SEARCHABLE_KEYS = new Set(["acoes", "lotacoes", "timeline"]);

  const rowMatchesFilter = (row: T, filterText: string) => {
    const q = removerAcentos(filterText.toLowerCase());
    return columns.some((col) => {
      const key = col.key as string;
      if (NON_SEARCHABLE_KEYS.has(key)) return false;

      if (col.render) {
        const rendered = removerAcentos(getPlainText(col.render(row)).toLowerCase());
        if (rendered.includes(q)) return true;
      }

      const value = getValue(row, key);
      if (value == null) return false;

      if (col.type === "date") {
        const br = removerAcentos(formatDateForFilter(value).toLowerCase());
        if (br.includes(q)) return true;
      }

      return removerAcentos(String(value).toLowerCase()).includes(q);
    });
  };

  const compareValues = (a: any, b: any, type: ColumnType = "string") => {
    if (a == null) a = "";
    if (b == null) b = "";
    switch (type) {
      case "number":
        return Number(a) - Number(b);
      case "date":
        return new Date(a).getTime() - new Date(b).getTime();
      default:
        return String(a).localeCompare(String(b), "pt-BR", { sensitivity: "base" });
    }
  };

  // Client-side: filtro
  const filteredData = useMemo(() => {
    if (serverSide)
      return data;
    if (!filter.trim())
      return data;

    return data?.filter((row) => rowMatchesFilter(row, filter.trim()));
  }, [data, filter, columns, serverSide]);

  // Client-side: ordenação
  const sortedData = useMemo(() => {
    if (serverSide)
      return data;

    if (filteredData) {

      const sorted = [...filteredData];
      const column = columns.find((c) => c.key === sortConfig.key);
      const type = column?.type || "string";

      sorted.sort((a, b) => {
        const valA = getValue(a, sortConfig.key) ?? "";
        const valB = getValue(b, sortConfig.key) ?? "";
        const result = compareValues(valA, valB, type);
        return sortConfig.direction === "ASC" ? result : -result;
      });
      return sorted;
    }
  }, [filteredData, sortConfig, columns, serverSide]);

  // Client-side: paginação
  const paginatedData = useMemo(() => {
    if (serverSide)
      return data;

    const start = (currentPage - 1) * pageSize;
    return sortedData?.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize, serverSide, data]);

  const total = serverSide && totalRecords !== undefined ? totalRecords : filteredData?.length;
  const totalPages = Math.ceil((total || 0) / pageSize);

  const getPageNumbers = () => {
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = start + maxVisible - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const requestSort = (key: string) => {
    let direction: "ASC" | "DESC" = "ASC";
    if (sortConfig.key === key && sortConfig.direction === "ASC") {
      direction = "DESC";
    }
    setSortConfig({ key, direction });
  };

  const getVisibleData = () => {
    const visibleCols = columns.filter((col) => visibleColumns.includes(col.key as string));
    const header = visibleCols.map((c) => c.header);

    const rows = (sortedData ?? [])
      .map((row) => visibleCols
        .map((c) => {
          const val = c.render ? getPlainText(c.render(row)) : getValue(row, c.key as string);
          return val !== undefined && val !== null ? String(val) : "";
        })
      );

    return { header, rows };
  };

  const printReactToPdf = useReactToPrint({
    contentRef: tableRef,
    pageStyle: `
        @page { size: landscape }; }
        body { -webkit-print-color-adjust: exact; }
      `,
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        {showFilter && <input
          id="datatablefilters"
          type="text"
          className="form-control form-control-sm w-25"
          placeholder="Filtrar..."
          value={filter ?? ''}
          onChange={(e) => {
            setFilter(e.target.value);
            setCurrentPage(1);
          }}
        />}

        {renderFilters?.()}

        <div className="d-flex align-items-center">
          {!disablePagination && <div className="col-auto form-check mt-2 me-2" title="Exibir barras de rolagem verticais.">
            <input
              id="scrollbars"
              className="form-check-input"
              type="checkbox"
              checked={scrollBars}
              onChange={(e) => { setScrollBars(e.target.checked); }} />
            <label htmlFor="scrollbars" className="form-check-label">
              Barras
            </label>
          </div>}

          {!disablePagination && <select
            id="pagesize"
            value={pageSize}
            title="Itens por Página."
            className="form-select form-select-sm w-auto me-3"
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>}

          {!disableColumnsFilter &&
            <div className="dropdown me-3" title="Exibir/ocultar colunas.">
              <button type="button" data-bs-toggle="dropdown" data-bs-auto-close="outside" className="btn btn-sm btn-outline-secondary dropdown-toggle" >
                Colunas
              </button>
              <div className="dropdown-menu ps-2">
                {columns.map((col) => {
                  const colKey = String(col.key);

                  return (
                    <li key={col.key as string} className="form-check" title={`${col.header}`}>
                      <input type="checkbox" className="form-check-input" id={`col-${colKey}`} checked={visibleColumns.includes(colKey)}
                        onChange={() => toggleColumn(colKey)}
                      />
                      <label className="form-check-label small" htmlFor={`col-${colKey}`}>
                        {col.header}
                      </label>
                    </li>
                  )
                })}
              </div>
            </div>
          }

          {!disableExportButtons &&
            <div className="dropdown me-3" title="Exportar tabela.">
              <button
                type="button"
                data-bs-toggle="dropdown"
                className="btn btn-sm btn-outline-secondary dropdown-toggle"
              >
                Exportar
              </button>
              <div className="dropdown-menu">
                <button title="Planilha do Excel." className="dropdown-item" onClick={() => { const { header, rows } = getVisibleData(); exportFromArrayToExcel(header, rows) }}>
                  Excel
                </button>
                <hr className="m-0 p-0 mb-1 mt-1" />
                <button title="Estável." className="dropdown-item" onClick={printReactToPdf} >
                  PDF Automático
                </button>
                <button title="Rápido." className="dropdown-item" onClick={() => { const { header, rows } = getVisibleData(); exportFromArrayToPdf(header, rows) }}>
                  PDF Simples
                </button>
              </div>
            </div>
          }

        </div>
      </div>

      <div className={`${scrollBars ? 'tabela' : ''}`}>
        <table className="table table-sm table-striped table-hover" ref={tableRef}>
          <thead>
            <tr>
              {columns
                .filter((col) => visibleColumns.includes(col.key as string))
                .map((col) => (
                  <th
                    key={col.key as string}
                    onClick={() => requestSort(col.key as string)}
                    style={{ cursor: "pointer" }}
                    colSpan={col.colSpan || 1}
                  >
                    {col.header}{" "}
                    {sortConfig.key === col.key && (
                      <span>{sortConfig.direction === "ASC" ? "▲" : "▼"}</span>
                    )}
                  </th>
                ))}
              {renderSubRow && (
                <th>Detalhes</th>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedData && paginatedData.map((row, i) => {
              const isExpanded = expandedRows.has(i);
              const subRow = renderSubRow?.(row);
              const rowClassName = getRowClassName?.(row);
              const rowStyle = getRowStyle?.(row);

              return (
                <React.Fragment key={i}>
                  <tr className={rowClassName} style={rowStyle}>
                    {columns
                      .filter((col) => visibleColumns.includes(col.key as string))
                      .map((col) => (
                        <td key={col.key as string}>
                          {col.render ? col.render(row) : getValue(row, col.key as string) ?? ""}
                        </td>
                      ))
                    }

                    {subRow && (
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm p-0 ps-3 pe-3"
                          onClick={() => toggleRow(i)}
                          title={isExpanded ? "Recolher" : "Expandir"}
                        >
                          {isExpanded ? "▲" : "▼"}
                        </button>
                      </td>
                    )}
                  </tr>

                  {isExpanded && subRow && (
                    <tr className="table-subrow">
                      <td colSpan={columns.length + 1}>
                        <div className="subrow-content">
                          {subRow}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

          </tbody>
        </table>
      </div>

      <div className="text-end text-muted mb-2">
        Total de {total} registros.
      </div>

      {!disablePagination && <nav className="d-flex justify-content-center mt-3 pagination">
        <div className="btn-group">
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          >
            Anterior
          </button>

          {getPageNumbers().map((num) => (
            <span key={num} className={`page-item ${currentPage === num ? 'active' : ''}`} >
              <button className="page-link page-link-secondary" onClick={() => setCurrentPage(num)}>
                {num}
              </button>
            </span>
          ))}

          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p: number) => Math.min(p + 1, totalPages))}
          >
            Próximo
          </button>
        </div>
      </nav>}

    </div>
  );
}